/**
 * Security utilities for origin validation and XSS prevention
 */
import { debugLog } from "./utils";

/**
 * Validates if an origin is allowed based on whitelist
 * @param {string} origin - The origin to validate
 * @param {string[]} allowedOrigins - Array of allowed origins
 * @returns {boolean} - True if origin is allowed
 */
export const isValidOrigin = (origin, allowedOrigins = []) => {
  if (!origin || typeof origin !== 'string') return false;

  // Always allow same origin
  if (origin === window.location.origin) return true;

  // Check against whitelist
  return allowedOrigins.some(allowed => {
    // Support wildcard patterns
    if (allowed.includes('*')) {
      // Escape regex metacharacters before converting wildcards
      const escaped = allowed.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
      const pattern = escaped.replace(/\\\*/g, '.*');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(origin);
    }
    return allowed === origin;
  });
};

/**
 * Secure postMessage wrapper with origin validation
 * @param {any} message - Message to send
 * @param {string} targetOrigin - Target origin
 * @param {string[]} allowedOrigins - Allowed origins whitelist
 */
export const securePostMessage = (message, targetOrigin = '*') => {
  try {
    const sanitizedMessage = typeof message === 'string' ? message : JSON.stringify(message);
    // The browser's postMessage API already enforces that the message is only
    // delivered if the recipient window's origin matches targetOrigin.
    // No need to block outgoing messages — origin validation belongs on the
    // receiving side (incoming message listeners), not the sending side.
    window.parent.postMessage(sanitizedMessage, targetOrigin);
  } catch (error) {
    debugLog('Failed to send postMessage:', error);
  }
};

/**
 * Escapes HTML entities to prevent XSS attacks.
 * Converts special characters (&, <, >, ", ') into HTML entities.
 * NOTE: This does NOT sanitize rich HTML — it escapes ALL tags.
 * @param {string} html - String to escape
 * @returns {string} - Escaped string safe for HTML insertion
 */
export const escapeHtml = (html) => {
  if (!html || typeof html !== 'string') return '';

  // Create a temporary element to parse HTML
  const temp = document.createElement('div');
  temp.textContent = html;
  return temp.innerHTML;
};

/**
 * Validates and sanitizes user input
 * @param {string} input - User input to validate
 * @param {Object} options - Validation options
 * @returns {string} - Sanitized input
 */
export const validateInput = (input, options = {}) => {
  if (!input || typeof input !== 'string') return '';

  const {
    maxLength = 1000,
    allowHtml = false,
    trimWhitespace = true
  } = options;

  let sanitized = input;

  // Trim whitespace if enabled
  if (trimWhitespace) {
    sanitized = sanitized.trim();
  }

  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  // Sanitize HTML if not allowed
  if (!allowHtml) {
    sanitized = escapeHtml(sanitized);
  }

  return sanitized;
};

/**
 * Content Security Policy helpers
 */
export const CSPHelpers = {
  /**
   * Gets a secure nonce for inline scripts
   * @returns {string} - Random nonce
   */
  generateNonce: () => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  },

  /**
   * Validates if a URL is safe for loading resources
   * @param {string} url - URL to validate
   * @returns {boolean} - True if URL is safe
   */
  isSafeUrl: (url) => {
    if (!url || typeof url !== 'string') return false;

    try {
      const urlObj = new URL(url);
      // Only allow https and relative URLs
      return urlObj.protocol === 'https:' || !urlObj.protocol;
    } catch {
      return false;
    }
  }
};